import { API } from '../api.js';
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import LevelSettingsModal from '../components/LevelSettingsModal';
import LevelsOverviewCard from '../components/LevelsOverviewCard';
import styles from './LeaderboardTab.module.css';

const DEFAULT_LEVELS = [
  { level: 1, label: 'Newcomer',    color: '#f6d365', icon: '1', perks: ['Join the community'],          xpRequired: 0     },
  { level: 2, label: 'Explorer',    color: '#43a0e9', icon: '2', perks: ['Unlock Direct Messaging'],     xpRequired: 500   },
  { level: 3, label: 'Contributor', color: '#43e97b', icon: '3', perks: ['Access to free resources'],    xpRequired: 1200  },
  { level: 4, label: 'Builder',     color: '#6c63ff', icon: '4', perks: ['Exclusive member badge'],      xpRequired: 2200  },
  { level: 5, label: 'Achiever',    color: '#f7971e', icon: '5', perks: ['Pin posts to community feed'], xpRequired: 3500  },
  { level: 6, label: 'Expert',      color: '#ff6b6b', icon: '6', perks: ['1:1 strategy call'],           xpRequired: 5200  },
  { level: 7, label: 'Master',      color: '#ffd700', icon: '7', perks: ['Access to VIP Courses'],       xpRequired: 7200  },
  { level: 8, label: 'Legend',      color: '#ff4da6', icon: '8', perks: ['Featured in community'],       xpRequired: 9500  },
  { level: 9, label: 'Elite',       color: '#00f5ff', icon: '9', perks: ['Free 1:1 call with founder'],  xpRequired: 12500 },
];

export default function LeaderboardTab({ community }) {
  const { user } = useAuth();

  const [activeTooltip,      setActiveTooltip]      = useState(null);
  const [editingLevelNumber, setEditingLevelNumber] = useState(null);
  const [data7d,   setData7d]   = useState([]);
  const [data30d,  setData30d]  = useState([]);
  const [dataAll,  setDataAll]  = useState([]);
  const [distribution,  setDistribution]  = useState({});
  const [totalMembers,  setTotalMembers]  = useState(0);
  const [loading, setLoading] = useState(true);

  const isOwner = user?.id?.toString() === community?.ownerId?.toString();

  /* ── parse level configs ─────────────────────────────────────────────── */
  let levels = [...DEFAULT_LEVELS];
  if (community?.levelConfigs) {
    try {
      const parsed = JSON.parse(community.levelConfigs);
      if (Array.isArray(parsed) && parsed.length === 9) levels = parsed;
    } catch (_) {}
  }

  const xpThresholds = [
    0,
    levels.find(l => l.level === 2)?.xpRequired ?? 500,
    levels.find(l => l.level === 3)?.xpRequired ?? 1200,
    levels.find(l => l.level === 4)?.xpRequired ?? 2200,
    levels.find(l => l.level === 5)?.xpRequired ?? 3500,
    levels.find(l => l.level === 6)?.xpRequired ?? 5200,
    levels.find(l => l.level === 7)?.xpRequired ?? 7200,
    levels.find(l => l.level === 8)?.xpRequired ?? 9500,
    levels.find(l => l.level === 9)?.xpRequired ?? 12500,
    999999,
  ];

  /* ── fetch leaderboard data ──────────────────────────────────────────── */
  useEffect(() => {
    if (!community?.id) return;
    setLoading(true);
    Promise.all([
      fetch(`${API}/api/community/members?communityId=${community.id}&timeframe=7d`).then(r => r.json()),
      fetch(`${API}/api/community/members?communityId=${community.id}&timeframe=30d`).then(r => r.json()),
      fetch(`${API}/api/community/members?communityId=${community.id}&timeframe=all`).then(r => r.json()),
    ]).then(([r7, r30, rAll]) => {
      const parse = r => Array.isArray(r) ? r : (r.items || []);
      const map   = m => ({
        id:     m.id,
        name:   m.displayName,
        avatar: m.avatarEmoji || m.avatarUrl || '👤',
        level:  m.level  || 1,
        xp:     m.totalXp || 0,
        score:  m.periodXp || 0,
        badge:  m.level >= 7 ? '👑' : m.level >= 5 ? '🏅' : m.level >= 3 ? '✍️' : '',
      });
      setData7d(parse(r7).map(map));
      setData30d(parse(r30).map(map));
      setDataAll(parse(rAll).map(map));
      if (!Array.isArray(rAll) && rAll.distribution) {
        setDistribution(rAll.distribution);
        setTotalMembers(rAll.totalMembers || 1);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [community?.id]);

  /* ── derive distribution from dataAll if backend didn't return it ──── */
  const effectiveDistribution = Object.keys(distribution).length > 0
    ? distribution
    : dataAll.reduce((acc, m) => {
        const lv = String(m.level || 1);
        acc[lv] = (acc[lv] || 0) + 1;
        return acc;
      }, {});

  const effectiveTotalMembers = totalMembers || dataAll.length || 1;

  /* ── derived values ──────────────────────────────────────────────────── */
  const myMember         = dataAll.find(m => String(m.id) === String(user?.id));
  const currentUserXp    = myMember?.xp    || user?.xp    || 0;
  const currentUserLevel = myMember?.level || user?.level || 1;
  const xpToNext         = xpThresholds[currentUserLevel] || 500;
  const prevThreshold    = xpThresholds[currentUserLevel - 1] || 0;
  const progressPct      = xpToNext > prevThreshold
    ? Math.min(100, Math.round(((currentUserXp - prevThreshold) / (xpToNext - prevThreshold)) * 100))
    : 100;
  const ptsToLevelUp = Math.max(0, xpToNext - currentUserXp);

  const enrollXp = community?.enrollCourseXp ?? 100;
  const postXp   = community?.postXp         ?? 30;
  const commXp   = community?.commentXp      ?? 15;
  const lPostXp  = community?.likePostXp     ?? 10;
  const lCommXp  = community?.likeCommentXp  ?? 5;

  const currentTier   = levels.find(l => l.level === currentUserLevel) || levels[0];
  const currentAvatar = user?.avatar_url ? `${API}${user.avatar_url}` : null;

  if (loading) {
    return (
      <div className={styles.loadingWrap}>
        <div className={styles.spinner} />
        <span>Loading leaderboard…</span>
      </div>
    );
  }

  return (
    <div className={styles.root}>

      {/* ── Profile + Levels card ─────────────────────────────────────── */}
      <div className={styles.profileCard}>

        {/* Left — user profile */}
        <div className={styles.profileLeft}>
          <div className={styles.avatarWrap}>
            {currentAvatar
              ? <img src={currentAvatar} alt="avatar" className={styles.avatarImg} />
              : <div className={styles.avatarFallback}>
                  {user?.display_name?.[0]?.toUpperCase() || '?'}
                </div>
            }
            <div className={styles.levelBadge} style={{ background: currentTier.color }}>
              {currentUserLevel}
            </div>
          </div>

          <div className={styles.profileName}>{user?.display_name || 'Anonymous'}</div>

          <div className={styles.tierLabel} style={{ color: currentTier.color }}>
            {currentTier.label}
          </div>

          {/* XP progress bar */}
          <div className={styles.xpBarWrap}>
            <div className={styles.xpBarTrack}>
              <div className={styles.xpBarFill} style={{ width: `${progressPct}%`, background: currentTier.color }} />
            </div>
            <div className={styles.xpBarLabels}>
              <span>{currentUserXp.toLocaleString()} XP</span>
              <span>{xpToNext === 999999 ? 'MAX' : xpToNext.toLocaleString()}</span>
            </div>
          </div>

          <div className={styles.ptsToLevel}>
            <span className={styles.ptsNum}>{ptsToLevelUp.toLocaleString()}</span>
            &nbsp;pts to level up
            <span
              className={styles.infoIcon}
              onMouseEnter={() => setActiveTooltip('info')}
              onMouseLeave={() => setActiveTooltip(null)}
            >ⓘ</span>

            {activeTooltip === 'info' && (
              <div className={styles.tooltip}>
                Earn XP: enroll (+{enrollXp}), post (+{postXp}), comment (+{commXp}),
                like post (+{lPostXp}), like comment (+{lCommXp})
              </div>
            )}
          </div>
        </div>

        {/* Right — levels grid */}
        <div className={styles.levelsGrid}>
          {levels.map(tier => {
            const unlocked  = currentUserLevel >= tier.level;
            const isCurrent = currentUserLevel === tier.level;
            const count      = distribution[tier.level.toString()] || 0;
            const pct        = totalMembers > 0 ? Math.round((count / totalMembers) * 100) : 0;
            const reqXp      = tier.xpRequired ?? xpThresholds[tier.level - 1] ?? 0;
            const remaining  = Math.max(0, reqXp - currentUserXp);

            return (
              <div key={tier.level} className={styles.tierRow}>
                {/* Tier icon */}
                <div
                  className={`${styles.tierDot} ${isCurrent ? styles.tierDotCurrent : unlocked ? styles.tierDotUnlocked : styles.tierDotLocked}`}
                  style={unlocked ? { borderColor: tier.color, background: isCurrent ? tier.color : 'var(--surface2)' } : {}}
                  title={isOwner ? 'Click to edit' : undefined}
                  onClick={() => isOwner && setEditingLevelNumber(tier.level)}
                  onMouseEnter={() => !unlocked && setActiveTooltip(tier.level)}
                  onMouseLeave={() => setActiveTooltip(null)}
                >
                  <span style={{ color: isCurrent ? '#fff' : unlocked ? tier.color : 'var(--text3)' }}>
                    {unlocked ? (tier.icon || tier.level) : '🔒'}
                  </span>

                  {/* Locked tooltip */}
                  {!unlocked && activeTooltip === tier.level && (
                    <div className={styles.tierTooltip}>
                      <div className={styles.tierTooltipTitle}>
                        Level {tier.level} — {reqXp.toLocaleString()} XP
                      </div>
                      <div className={styles.tierTooltipBody}>
                        Need <strong>{remaining.toLocaleString()}</strong> more XP:
                      </div>
                      <ul className={styles.tierTooltipList}>
                        <li><strong>{Math.ceil(remaining / enrollXp).toLocaleString()}</strong> enrollments (+{enrollXp})</li>
                        <li><strong>{Math.ceil(remaining / postXp).toLocaleString()}</strong> posts (+{postXp})</li>
                        <li><strong>{Math.ceil(remaining / commXp).toLocaleString()}</strong> comments (+{commXp})</li>
                      </ul>
                    </div>
                  )}
                </div>

                {/* Tier info */}
                <div className={styles.tierInfo}>
                  <div className={styles.tierName}>{tier.label}</div>
                  <div className={styles.tierMeta}>
                    {tier.perks[0] && <span className={styles.tierPerk}>"{tier.perks[0]}"</span>}
                    <span className={styles.tierPct}>{pct}% of members</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Owner-only: Levels Overview Card ────────────────────────────── */}
      {isOwner && (
        <LevelsOverviewCard
          community={community}
          levels={levels}
          distribution={effectiveDistribution}
          totalMembers={effectiveTotalMembers}
          onSaved={() => window.location.reload()}
        />
      )}

      {/* ── Timestamp ────────────────────────────────────────────────────── */}
      <div className={styles.timestamp}>
        Last updated: {new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true })}
      </div>

      {/* ── Three leaderboard columns ─────────────────────────────────────── */}
      <div className={styles.columns}>
        <LeaderboardColumn title="7-Day"    data={data7d}   showPlus />
        <LeaderboardColumn title="30-Day"   data={data30d}  showPlus />
        <LeaderboardColumn title="All-Time" data={dataAll}  showPlus={false} />
      </div>

      {/* Level editor modal — only used when clicking dots directly (non-owner path) */}
      {editingLevelNumber && !isOwner && (
        <LevelSettingsModal
          levelNumber={editingLevelNumber}
          community={community}
          onClose={() => setEditingLevelNumber(null)}
          onSaved={() => { setEditingLevelNumber(null); window.location.reload(); }}
        />
      )}
    </div>
  );
}

/* ── Leaderboard Column ──────────────────────────────────────────────────── */
function LeaderboardColumn({ title, data, showPlus }) {
  const ranked = [...data].sort((a, b) => b.score - a.score).slice(0, 50);

  return (
    <div className={styles.colCard}>
      <div className={styles.colHeader}>{title}</div>

      <div className={styles.colBody}>
        {ranked.length === 0 && (
          <div className={styles.noData}>No data yet</div>
        )}

        {ranked.map((u, i) => {
          const isTop3 = i < 3;
          const medals = ['🥇', '🥈', '🥉'];

          return (
            <div key={u.id} className={`${styles.colRow} ${isTop3 ? styles.colRowTop : ''}`}>
              {/* Rank */}
              <div className={styles.colRank}>
                {isTop3
                  ? <span className={styles.medal}>{medals[i]}</span>
                  : <span className={styles.rankNum}>{i + 1}</span>
                }
              </div>

              {/* Avatar */}
              <div className={styles.colAvatar}>
                {u.avatar && u.avatar.startsWith('/')
                  ? <img src={`${API}${u.avatar}`} alt="" className={styles.colAvatarImg} />
                  : <span className={styles.colAvatarEmoji}>{u.avatar || '👤'}</span>
                }
              </div>

              {/* Name */}
              <div className={styles.colName}>
                {u.name} {u.badge}
              </div>

              {/* Score */}
              <div className={styles.colScore}>
                {showPlus && <span className={styles.scorePlus}>+</span>}
                {u.score.toLocaleString()}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
